import { useEffect, useState } from 'react';
import {
  createFrontendModule,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { ghDataProviderApiRef } from '../ghDataProviderApi.ts';

const REPO = 'github-actions-public';
const DIR = '.github/caller-templates';

// Matches the backend `SAFE_FILENAME` extension check in fetchCallerWorkflows.ts
// so a `.yaml` caller template is listed here as well as accepted on submit.
const WORKFLOW_FILE = /\.ya?ml$/;

interface WorkflowOption {
  filename: string;
  name: string;
  description: string;
}

function extractYamlField(content: string, field: string): string | undefined {
  const match = content.match(
    new RegExp(`^${field}:\\s*['"]?(.+?)['"]?\\s*$`, 'm'),
  );
  return match?.[1]?.trim();
}

function GithubWorkflowsPickerComponent({
  onChange,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<string[]>) {
  const dataProvider = useApi(ghDataProviderApiRef);
  const [workflows, setWorkflows] = useState<WorkflowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const selected: string[] = formData ?? [];
  const heading =
    typeof uiSchema?.['ui:options']?.heading === 'string'
      ? uiSchema['ui:options'].heading
      : undefined;

  useEffect(() => {
    async function load() {
      const files = await dataProvider.listFilesInRepoFolder(REPO, DIR);

      const options = await Promise.all(
        files
          .filter(f => WORKFLOW_FILE.test(f.name))
          .map(async f => {
            const content = await (await fetch(f.downloadUrl)).text();
            return {
              filename: f.name,
              name: extractYamlField(content, 'name') ?? f.name,
              description: extractYamlField(content, 'description') ?? '',
            };
          }),
      );

      setWorkflows(options);
    }

    load()
      .catch(e => setError(String(e.message)))
      .finally(() => setLoading(false));
  }, [dataProvider]);

  const toggle = (filename: string) => {
    const next = selected.includes(filename)
      ? selected.filter(f => f !== filename)
      : [...selected, filename];
    onChange(next);
  };

  if (loading) return <p>Laster tilgjengelige workflows...</p>;
  if (error) return <p>Feil ved lasting av workflows: {error}</p>;
  if (!workflows.length)
    return <p>Ingen workflows funnet i github-actions-public.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {heading && (
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '4px', lineHeight: 1.5 }}>{heading}</div>
      )}
      {workflows.map(w => (
        <label
          key={w.filename}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={selected.includes(w.filename)}
            onChange={() => toggle(w.filename)}
            style={{ marginTop: '3px', flexShrink: 0 }}
          />
          <div>
            <strong>{w.name}</strong>
            {w.description && (
              <p
                style={{ margin: '2px 0 0', color: '#666', fontSize: '0.875rem' }}
              >
                {w.description}
              </p>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

const githubWorkflowsPickerExtension = FormFieldBlueprint.make({
  name: 'github-workflows-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'GithubWorkflowsPicker',
        component: GithubWorkflowsPickerComponent,
      }),
  },
});

export const scaffolderWorkflowModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [githubWorkflowsPickerExtension],
});
